import {
  PathsV1VehicleBuyInSearchPostParametersQueryExtend,
  PathsV1VehicleBuyInSearchPostParametersQueryOrderByDirection,
  PathsV1VehicleBuyInSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import type {
  VehicleBuyInSearchBody,
  VehicleBuyInSearchParameters,
} from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-search.query";
import { useVehicleBuyInSearchQuery } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-search.query";
import { VEHICLE_BUY_IN_RESOURCE } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-create-data.query";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import type { TableFeatures } from "@/components/data-table/types";
import { createBasePredefinedTable } from "../_components/base-predefined-table";
import type {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "../_components/base-predefined-table.types";
import { vehicleBuyInTableDefaultColumns, type VehicleBuyInRow } from "./vehicle-buy-in-table-columns";

type VehicleBuyInSorting = NonNullable<VehicleBuyInSearchParameters["orderBy"]>;

const VEHICLE_BUY_IN_PAGE_SIZE = 20;

const DEFAULT_EXTEND = [
  PathsV1VehicleBuyInSearchPostParametersQueryExtend.Vehicle,
  PathsV1VehicleBuyInSearchPostParametersQueryExtend.Vehicle_ModelGeneration,
  PathsV1VehicleBuyInSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
  PathsV1VehicleBuyInSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
  PathsV1VehicleBuyInSearchPostParametersQueryExtend.CreatedBy,
  PathsV1VehicleBuyInSearchPostParametersQueryExtend.Branch,
] as const;

const VEHICLE_BUY_IN_TABLE_CONFIG: PredefinedTableConfig<VehicleBuyInSorting> = {
  defaultPageSize: VEHICLE_BUY_IN_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1VehicleBuyInSearchPostParametersQueryOrderByDirection.Desc,
      field: PathsV1VehicleBuyInSearchPostParametersQueryOrderByField.CreatedAt,
    },
  ],
  defaultTableConfig: {
    searchPlaceholder: "fab:SearchVehicleBuyInVin",
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  },
  defaultRowActions: {
    showEdit: false,
    showDelete: false,
    showView: true,
    showRestore: false,
  },
  sortingMapping: {
    customer: [PathsV1VehicleBuyInSearchPostParametersQueryOrderByField.CustomerName],
    employee: [
      PathsV1VehicleBuyInSearchPostParametersQueryOrderByField.EmployeeLastName,
      PathsV1VehicleBuyInSearchPostParametersQueryOrderByField.EmployeeFirstName,
    ],
    type: [PathsV1VehicleBuyInSearchPostParametersQueryOrderByField.BuyInType],
  },
};

const VEHICLE_BUY_IN_TABLE_QUERY_HOOKS: DataFetchHooks<
  VehicleBuyInRow,
  VehicleBuyInSearchParameters,
  VehicleBuyInSearchBody
> = {
  useSearchQuery: useVehicleBuyInSearchQuery,
};

export type VehicleBuyInTableProps = BasePredefinedTableProps<
  VehicleBuyInRow,
  VehicleBuyInSorting,
  VehicleBuyInSearchParameters,
  VehicleBuyInSearchBody
>;

const BaseVehicleBuyInTable = createBasePredefinedTable({
  config: VEHICLE_BUY_IN_TABLE_CONFIG,
  queryHooks: VEHICLE_BUY_IN_TABLE_QUERY_HOOKS,
  defaultColumns: vehicleBuyInTableDefaultColumns,
});

export function VehicleBuyInTable(props: VehicleBuyInTableProps) {
  const {
    name = `${VEHICLE_BUY_IN_RESOURCE}-table`,
    extend = DEFAULT_EXTEND,
    onRowNavigate = createRowNavigatePath<VehicleBuyInRow>("/fab/buy-in"),
    ...restProps
  } = props;

  return <BaseVehicleBuyInTable name={name} extend={extend} onRowNavigate={onRowNavigate} {...restProps} />;
}
